import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Card,
  Badge,
  BadgeText,
} from '@gluestack-ui/themed';
import type { Task } from '@/common/api/models/task';

/**
 * MCQ result data shape expected from the grade report API.
 * This data becomes available after a proctored exam closes and the module is graded.
 */
export interface MCQQuestionResult {
  id: string;
  text: string;
  explanation?: string | null;
  options: {
    id: string;
    text: string;
    isCorrect: boolean;
  }[];
}

export interface MCQResultData {
  questions: MCQQuestionResult[];
  studentAnswers: Record<string, string>;
  score: number;
  maxScore: number;
  passed: boolean;
}

interface MCQResultsCardProps {
  task: Task;
  mcqResult?: MCQResultData | null;
}

const ScoreSummary: React.FC<{
  score: number;
  maxScore: number;
  passed: boolean;
  passThresholdPercent: number;
}> = ({
  score,
  maxScore,
  passed,
  passThresholdPercent,
}) => {
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  const requiredCorrect = maxScore > 0
    ? Math.ceil((passThresholdPercent / 100) * maxScore)
    : 0;
  const thresholdLabel = maxScore > 0
    ? `Pass threshold: ${passThresholdPercent}% (need ${requiredCorrect} correct)`
    : `Pass threshold: ${passThresholdPercent}%`;

  return (
    <Box
      testID="mcq-grade-score-summary"
      bg={passed ? '$success50' : '$error50'}
      borderRadius="$md"
      p="$4"
      borderWidth={1}
      borderColor={passed ? '$success300' : '$error300'}
    >
      <HStack justifyContent="space-between" alignItems="center">
        <VStack>
          <Text size="sm" color={passed ? '$success700' : '$error700'} bold>
            {passed ? 'Passed' : 'Not Passed'}
          </Text>
          <Text size="xs" color="$textSubtle">
            {thresholdLabel}
          </Text>
        </VStack>
        <VStack alignItems="flex-end">
          <Text size="xl" bold color={passed ? '$success700' : '$error700'}>
            {score}
            {' / '}
            {maxScore}
          </Text>
          <Text size="xs" color="$textSubtle">
            {percentage}
            %
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

const QuestionFeedbackItem: React.FC<{
  question: MCQQuestionResult;
  questionNumber: number;
  studentAnswerId?: string;
}> = ({ question, questionNumber, studentAnswerId }) => {
  const correctOption = question.options.find((o) => o.isCorrect);
  const isCorrect = studentAnswerId === correctOption?.id;

  return (
    <Box
      testID="mcq-grade-question-item"
      borderWidth={1}
      borderColor={isCorrect ? '$success200' : '$error200'}
      borderRadius="$md"
      p="$3"
      bg="$white"
    >
      <VStack space="sm">
        {/* Question header */}
        <HStack justifyContent="space-between" alignItems="center">
          <Text size="sm" color="$textSubtle">
            Question
            {' '}
            {questionNumber}
          </Text>
          <Badge
            bg={isCorrect ? '$success100' : '$error100'}
            borderRadius="$full"
            px="$2"
            py="$0.5"
          >
            <BadgeText
              size="xs"
              color={isCorrect ? '$success700' : '$error700'}
            >
              {isCorrect ? 'Correct' : 'Incorrect'}
            </BadgeText>
          </Badge>
        </HStack>

        {/* Question text */}
        <Text size="sm" bold>
          {question.text}
        </Text>

        {/* Options */}
        <VStack space="xs">
          {question.options.map((option) => {
            const isStudentChoice = option.id === studentAnswerId;
            const isCorrectOption = option.isCorrect;

            let bg = '$white';
            let borderColor = '$borderLight200';
            let textColor = '$textBody';

            if (isCorrectOption) {
              bg = '$success50';
              borderColor = '$success300';
              textColor = '$success700';
            } else if (isStudentChoice && !isCorrectOption) {
              bg = '$error50';
              borderColor = '$error300';
              textColor = '$error700';
            }

            return (
              <Box
                key={option.id}
                bg={bg}
                borderWidth={1}
                borderColor={borderColor}
                borderRadius="$sm"
                px="$3"
                py="$2"
              >
                <HStack space="sm" alignItems="center">
                  <Text size="sm" color={textColor} flex={1}>
                    {option.text}
                  </Text>
                  {isCorrectOption && (
                    <Text size="xs" color="$success600" bold>
                      ✓
                    </Text>
                  )}
                  {isStudentChoice && !isCorrectOption && (
                    <Text size="xs" color="$error600" bold>
                      ✗
                    </Text>
                  )}
                </HStack>
              </Box>
            );
          })}
        </VStack>

        {/* Explanation */}
        {question.explanation && (
          <Box
            bg="$backgroundLight50"
            borderLeftWidth={3}
            borderLeftColor="$info400"
            p="$2"
            borderRadius="$sm"
          >
            <Text size="xs" color="$textSubtle">
              {question.explanation}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

const MCQResultsCard: React.FC<MCQResultsCardProps> = ({
  task,
  mcqResult,
}) => {
  const passThresholdPercent = task.mcqConfig?.passThresholdPercent ?? 70;

  if (!mcqResult) {
    // MCQ task without result data - show basic card
    return (
      <Card testID="mcq-grade-card" p="$4">
        <VStack space="sm">
          <Heading size="md">{task.title}</Heading>
          <Badge variant="outline" alignSelf="flex-start">
            <BadgeText size="xs">Multiple Choice Quiz</BadgeText>
          </Badge>
          {task.submission && (
            <Text size="sm" color="$textSubtle">
              Quiz submitted. Detailed results will be available after grading.
            </Text>
          )}
          {!task.submission && (
            <Text size="sm" color="$textSubtle">
              No submission for this quiz.
            </Text>
          )}
        </VStack>
      </Card>
    );
  }

  return (
    <Card testID="mcq-grade-card" p="$4">
      <VStack space="md">
        <Heading size="md">{task.title}</Heading>

        <ScoreSummary
          score={mcqResult.score}
          maxScore={mcqResult.maxScore}
          passed={mcqResult.passed}
          passThresholdPercent={passThresholdPercent}
        />

        {mcqResult.questions.map((question, index) => (
          <QuestionFeedbackItem
            key={question.id}
            question={question}
            questionNumber={index + 1}
            studentAnswerId={mcqResult.studentAnswers[question.id]}
          />
        ))}
      </VStack>
    </Card>
  );
};

export default MCQResultsCard;
